import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Wallet, TrendingUp, TrendingDown, DollarSign, Sparkles, Trophy, Users,
  Gift, Coins, Play, ShoppingBag, Link2, Handshake, UserPlus, Crown,
  Calendar, ChevronRight, Clock, ArrowUpRight, Info, Loader2
} from 'lucide-react';

// Icon per revenue source key (from wallet_hub.REVENUE_SOURCES)
const SOURCE_ICON = {
  qualified_views: Play,
  ads: TrendingUp,
  premium_subs: Crown,
  tips: DollarSign,
  coins: Coins,
  gifts: Gift,
  subscribers: Users,
  ppv: DollarSign,
  exclusive: Sparkles,
  livestream_tickets: Calendar,
  store: ShoppingBag,
  merchandise: ShoppingBag,
  affiliates: Link2,
  brand_deals: Handshake,
  referrals: UserPlus,
  monthly_bonus: Trophy,
  achievements: Trophy,
};

function money(n) {
  const num = Number(n || 0);
  if (num >= 1000) return `$${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `$${num.toFixed(2)}`;
}

function StatCard({ label, value, sub, tone = 'default', icon: Icon, testId }) {
  const tones = {
    default: 'from-card/60 to-card/40 border-border',
    good: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/30',
    warn: 'from-orange-500/10 to-orange-500/5 border-orange-500/30',
    hero: 'from-violet-600/20 via-fuchsia-600/10 to-card/60 border-violet-500/40',
  };
  return (
    <Card className={`bg-gradient-to-br ${tones[tone]} p-4 sm:p-5 rounded-2xl`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground/70 font-semibold">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground/50" />}
      </div>
      <p className={`font-bold tabular-nums ${tone === 'hero' ? 'text-3xl sm:text-4xl' : 'text-xl sm:text-2xl'}`}>
        {value}
      </p>
      {sub && <p className="text-[11px] text-muted-foreground/70 mt-1">{sub}</p>}
    </Card>
  );
}

export default function CreatorWalletPage() {
  const { getAuthHeaders, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    try {
      const [sRes, bRes, tRes, rRes] = await Promise.all([
        fetch(`${API}/wallet/summary`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/wallet/breakdown?days=${windowDays}`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/wallet/timeline?days=${windowDays}`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/wallet/recent?limit=20`, { headers }).then(r => r.ok ? r.json() : { items: [] }),
      ]);
      setSummary(sRes);
      setBreakdown(bRes);
      setTimeline(tRes);
      setRecent(rRes?.items || []);
    } catch {
      toast.error('Failed to load wallet');
    }
    setLoading(false);
  }, [getAuthHeaders, windowDays]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchAll();
  }, [authLoading, isAuthenticated, fetchAll, navigate]);

  if (loading && !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
      </div>
    );
  }

  const available = summary?.balances?.available || 0;
  const minPayout = summary?.next_payout?.min_payout_usd || 25;
  const canCash = summary?.next_payout?.can_cashout;
  const growth = summary?.growth_pct_vs_prev_30 || 0;

  const maxDay = timeline?.series?.reduce((m, d) => Math.max(m, d.amount_usd), 0) || 1;

  return (
    <div className="min-h-screen bg-background text-foreground pt-24 pb-16" data-testid="wallet-hub-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Creator Wallet</h1>
              <p className="text-xs text-muted-foreground">Every dollar you earn, in one place.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/creator/earnings')}
              variant="outline"
              size="sm"
              className="gap-1.5"
              data-testid="wallet-view-details"
            >
              Detailed report <ArrowUpRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* HERO Stats: Available + Cash Out */}
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-2">
            <StatCard
              label="Available to Cash Out"
              value={money(available)}
              sub={canCash ? `You can cash out now — min payout is ${money(minPayout)}` : `Reach ${money(minPayout)} to cash out`}
              tone="hero"
              icon={Wallet}
              testId="wallet-available-hero"
            />
          </div>
          <Card className="p-5 flex flex-col justify-between rounded-2xl bg-gradient-to-br from-emerald-500/10 to-card/40 border-emerald-500/30" data-testid="wallet-cashout-card">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-emerald-400 font-semibold mb-1">Cash Out</p>
              <p className="text-xs text-muted-foreground mb-3">Stripe payouts • min {money(minPayout)}</p>
            </div>
            <Button
              onClick={() => navigate('/creator/earnings')}
              disabled={!canCash}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-40"
              data-testid="wallet-cashout-btn"
            >
              {summary?.next_payout?.payout_connected ? 'Request Payout' : 'Connect Stripe'}
            </Button>
          </Card>
        </div>

        {/* Six time-window tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-6" data-testid="wallet-timeline-tiles">
          <StatCard label="Today" value={money(summary?.today?.total)} sub={`${summary?.today?.count || 0} txns`} testId="wallet-tile-today" />
          <StatCard label="Yesterday" value={money(summary?.yesterday?.total)} sub={`${summary?.yesterday?.count || 0} txns`} testId="wallet-tile-yesterday" />
          <StatCard label="Last 7 Days" value={money(summary?.last_7_days?.total)} sub={`${summary?.last_7_days?.count || 0} txns`} testId="wallet-tile-7d" />
          <StatCard label="Last 30 Days" value={money(summary?.last_30_days?.total)} sub={`${growth >= 0 ? '+' : ''}${growth}% vs prev`} tone={growth >= 0 ? 'good' : 'warn'} icon={growth >= 0 ? TrendingUp : TrendingDown} testId="wallet-tile-30d" />
          <StatCard label="This Year" value={money(summary?.ytd?.total)} sub={`${summary?.ytd?.count || 0} txns`} testId="wallet-tile-ytd" />
          <StatCard label="Lifetime" value={money(summary?.lifetime?.total_net)} sub={`${summary?.lifetime?.transaction_count || 0} total`} testId="wallet-tile-lifetime" />
        </div>

        {/* Balances row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-6" data-testid="wallet-balances">
          <StatCard label="Pending" value={money(summary?.balances?.pending)} sub={`Holds ${summary?.next_payout?.payout_hold_days || 7}d`} icon={Clock} testId="wallet-pending" />
          <StatCard label="Available" value={money(summary?.balances?.available)} tone="good" testId="wallet-available" />
          <StatCard label="Locked" value={money(summary?.balances?.locked)} icon={Info} testId="wallet-locked" />
          <StatCard label="Withdrawn" value={money(summary?.balances?.withdrawn)} sub="Lifetime paid out" testId="wallet-withdrawn" />
        </div>

        {/* Est tomorrow */}
        <Card className="p-4 sm:p-5 mb-6 rounded-2xl bg-gradient-to-r from-violet-500/10 via-card/40 to-card/40 border-violet-500/25" data-testid="wallet-tomorrow-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-violet-400" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-violet-400 font-semibold">Estimated Tomorrow</p>
                <p className="text-2xl font-bold tabular-nums">{money(summary?.next_payout?.estimated_tomorrow_usd)}</p>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground max-w-xs sm:text-right">Based on your average daily net over the last 7 days.</p>
          </div>
        </Card>

        {/* Timeline chart + breakdown */}
        <Tabs defaultValue="chart" className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <TabsList className="bg-card/60 border border-border">
              <TabsTrigger value="chart" data-testid="wallet-tab-chart">Chart</TabsTrigger>
              <TabsTrigger value="sources" data-testid="wallet-tab-sources">Sources</TabsTrigger>
              <TabsTrigger value="activity" data-testid="wallet-tab-activity">Activity</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-1">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold ${windowDays === d ? 'bg-violet-500/20 text-violet-300' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`wallet-window-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          <TabsContent value="chart">
            <Card className="p-5 rounded-2xl">
              <p className="text-xs text-muted-foreground mb-4">Daily net earnings (last {windowDays} days)</p>
              <div className="flex items-end gap-1 h-40" data-testid="wallet-chart-bars">
                {timeline?.series?.map((day, i) => {
                  const h = maxDay > 0 ? Math.max(2, (day.amount_usd / maxDay) * 100) : 2;
                  return (
                    <div key={i} className="flex-1 relative group">
                      <div
                        className="w-full rounded-t bg-gradient-to-t from-violet-600 to-fuchsia-500 transition-all hover:brightness-125"
                        style={{ height: `${h}%` }}
                      />
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 bg-black/90 border border-border rounded px-1.5 py-0.5 text-[10px] whitespace-nowrap pointer-events-none z-10">
                        {day.date} · {money(day.amount_usd)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="sources">
            <Card className="p-5 rounded-2xl">
              <p className="text-xs text-muted-foreground mb-4">Where the money came from (last {windowDays} days) · Total {money(breakdown?.total_usd)}</p>
              <ul className="space-y-3">
                {(breakdown?.sources || []).map(src => {
                  const Icon = SOURCE_ICON[src.key] || DollarSign;
                  return (
                    <li key={src.key} className="flex items-center gap-3" data-testid={`wallet-source-${src.key}`}>
                      <div className="w-8 h-8 rounded-lg bg-muted/30 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-violet-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold truncate">{src.label}</p>
                          <p className="text-sm font-bold tabular-nums">{money(src.amount_usd)}</p>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted/30 mt-1.5 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${src.percent}%` }} />
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">{src.count} txn · {src.percent}%</p>
                      </div>
                    </li>
                  );
                })}
                {(!breakdown?.sources?.length || breakdown.total_usd === 0) && (
                  <li className="text-center py-8 text-muted-foreground text-sm" data-testid="wallet-sources-empty">
                    No earnings yet — start posting content to unlock all 17 revenue streams.
                  </li>
                )}
              </ul>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <Card className="p-5 rounded-2xl">
              <p className="text-xs text-muted-foreground mb-4">Recent transactions</p>
              <ul className="divide-y divide-border/50">
                {recent.map((r) => (
                  <li key={r.entry_id} className="py-3 flex items-center justify-between gap-3" data-testid={`wallet-recent-${r.entry_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{r.source_label}</p>
                        <p className="text-[10px] text-muted-foreground">{r.from_user_name || 'Platform'} · {r.status}</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold tabular-nums text-emerald-400">+{money(r.net_amount_usd)}</p>
                  </li>
                ))}
                {!recent.length && (
                  <li className="py-8 text-center text-muted-foreground text-sm" data-testid="wallet-activity-empty">
                    No activity yet.
                  </li>
                )}
              </ul>
              <div className="mt-4 pt-3 border-t border-border/50">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/creator/earnings')}
                  className="w-full gap-1.5 text-xs"
                  data-testid="wallet-view-full-history"
                >
                  View full history <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
